// Shared hook, same shape as useResolvedPhotoUrls.ts (backlog DOCS-33) but
// for the single snapshotted logo path a document carries (backlog
// PRINT-2) rather than a list.
import { useEffect, useState } from "react";
import { resolveLogoUrl } from "@/lib/logoUpload";

/**
 * Resolves a document's snapshotted bizLogoPath to a fresh signed URL (the
 * account-files bucket is private — see logoUpload.ts's resolveLogoUrl,
 * never persisted so a document viewed weeks later still displays).
 * Fails silently (no toast) on a resolve error — a missing logo just falls
 * back to today's plain-text-only header, the same as a document with no
 * logo at all; this is decorative branding, not content worth alarming the
 * user is missing.
 *
 * `preResolved` (backlog PRINT-26): when passed — even as `null` — this
 * short-circuits network resolution entirely and returns that value as-is.
 * The print-only route (PrintDocumentPage) has no Supabase session to
 * resolve this itself; its Edge Function (print-doc-data) resolves the
 * signed URL server-side and hands it straight to this hook instead. Every
 * other caller omits the argument (stays `undefined`) and gets today's
 * unchanged authenticated-resolution behavior.
 */
export function useResolvedLogoUrl(path: string | undefined, preResolved?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (preResolved !== undefined) return;
    if (!path) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    resolveLogoUrl(path)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch((error: unknown) => {
        console.error("useResolvedLogoUrl: resolveLogoUrl failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [path, preResolved]);

  return preResolved !== undefined ? preResolved : url;
}
